import { useState } from "react";
import { Eye, EyeOff, Check, X } from "lucide-react";

export const PASSWORD_RULES = [
  ["len", "At least 8 characters", (p) => p.length >= 8],
  ["num", "Contains a number", (p) => /\d/.test(p)],
  ["sym", "Contains a symbol (e.g. ! ? # $)", (p) => /[^A-Za-z0-9]/.test(p)],
];
export const passwordValid = (p) => PASSWORD_RULES.every(([, , t]) => t(p || ""));
export const PASSWORD_RULE_TEXT = "Password must be at least 8 characters and include a number and a symbol.";

export function passwordStrength(p) {
  p = p || "";
  let score = 0;
  if (p.length >= 8) score++;
  if (p.length >= 12) score++;
  if (/\d/.test(p)) score++;
  if (/[^A-Za-z0-9]/.test(p)) score++;
  if (/[a-z]/.test(p) && /[A-Z]/.test(p)) score++;
  if (!p) return { score: 0, label: "", pct: 0 };
  if (!passwordValid(p)) return { score: Math.min(score, 1), label: "Too weak", pct: 20 };
  if (score <= 3) return { score: 2, label: "Fair", pct: 50 };
  if (score === 4) return { score: 3, label: "Good", pct: 75 };
  return { score: 4, label: "Strong", pct: 100 };
}

const BAR = ["bg-neutral-300", "bg-red-500", "bg-amber-500", "bg-lime-500", "bg-emerald-600"];

// Password input with show/hide eye. `meter` adds the live strength meter + rule checklist for NEW passwords.
export function PasswordField({ value, onChange, testId, className, inputClassName, meter = false, autoComplete, placeholder, required, variant = "staff", id }) {
  const [show, setShow] = useState(false);
  const emp = variant === "employee";
  const s = passwordStrength(value);
  return (
    <div className={className}>
      <div className="relative">
        <input id={id} data-testid={testId} type={show ? "text" : "password"} value={value} onChange={onChange} required={required} placeholder={placeholder}
          autoComplete={autoComplete || (meter ? "new-password" : "current-password")}
          className={inputClassName || (emp ? "emp-input pr-12" : "mt-1.5 w-full border border-input bg-white px-3 py-2.5 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-black")} />
        <button type="button" data-testid={`${testId}-toggle`} onClick={() => setShow((v) => !v)} aria-label={show ? "Hide password" : "Show password"} tabIndex={-1}
          className={`absolute right-3 ${emp ? "top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" : "top-1/2 -translate-y-1/2 mt-[3px] text-muted-foreground hover:text-black"} transition-colors`}>
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {meter && (
        <div className="mt-2" data-testid={`${testId}-meter`}>
          <div className="flex items-center gap-2">
            <div className={`h-1.5 flex-1 ${emp ? "rounded-full bg-slate-100" : "bg-neutral-200"} overflow-hidden`}>
              <div className={`h-full ${BAR[s.score]} transition-[width] duration-300`} style={{ width: `${s.pct}%` }} />
            </div>
            <span data-testid={`${testId}-strength`} className={`text-[10px] font-semibold uppercase tracking-widest ${s.score >= 3 ? "text-emerald-700" : s.score === 2 ? "text-amber-700" : "text-red-600"}`}>{s.label}</span>
          </div>
          <ul className="mt-1.5 grid gap-0.5 text-[11px]">
            {PASSWORD_RULES.map(([k, label, test]) => {
              const ok = test(value || "");
              return <li key={k} data-testid={`${testId}-rule-${k}`} className={`flex items-center gap-1.5 ${ok ? "text-emerald-700" : "text-muted-foreground"}`}>{ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}{label}</li>;
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
